import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MessageCircle, Sparkles, PenLine, Reply, Trash2, Loader2, CornerDownRight, Edit } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MentionText } from './MentionInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_DEPTH = 3;

const REVIEW_REACTIONS = [
  { key: 'insightful', icon: '\u{1F33F}', label: 'Insightful' },
  { key: 'beautiful', icon: '\u{2728}', label: 'Beautifully Noticed' },
  { key: 'helpful', icon: '\u{1F50E}', label: 'Helpful Suggestion' },
  { key: 'thoughtful', icon: '\u{1F4A1}', label: 'Thoughtful Perspective' },
  { key: 'kind', icon: '\u{1F90D}', label: 'Kind Voice' },
];

const ReviewReactions = ({ commentId }) => {
  const { user, api } = useAuth();
  const [myReaction, setMyReaction] = useState(null);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchReactions = async () => {
      try {
        const url = user
          ? `/comments/${commentId}/reactions?user_id=${user.id}`
          : `/comments/${commentId}/reactions`;
        const res = await api.get(url);
        setCounts(res.data.counts || {});
        setMyReaction(res.data.my_reaction);
      } catch { /* non-critical */ }
    };
    fetchReactions();
  }, [api, commentId, user]);

  const handleReact = async (reactionKey) => {
    if (!user || loading) return;
    setLoading(true);
    try {
      if (myReaction === reactionKey) {
        const res = await api.delete(`/comments/${commentId}/react`);
        setMyReaction(null);
        setCounts(res.data.counts || {});
      } else {
        const res = await api.post(`/comments/${commentId}/react`, { reaction: reactionKey });
        setMyReaction(reactionKey);
        setCounts(res.data.counts || {});
      }
    } catch { /* non-critical */ }
    setLoading(false);
  };

  const totalReactions = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={`review-reactions-${commentId}`}>
      {REVIEW_REACTIONS.map((r) => {
        const count = counts[r.key] || 0;
        const isActive = myReaction === r.key;
        const showPill = isActive || count > 0;

        return showPill || user ? (
          <div key={r.key} className="relative group">
            <button
              type="button"
              onClick={() => handleReact(r.key)}
              disabled={!user || loading}
              className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border transition-all duration-200 ${
                isActive
                  ? 'border-brand-primary/25 bg-brand-primary/[0.06] text-brand-primary'
                  : 'border-transparent text-text-muted/60 hover:border-brand-primary/15 hover:bg-surface-alt hover:text-text-muted'
              } ${!user ? 'cursor-default' : 'cursor-pointer'} ${loading ? 'opacity-50' : ''}`}
              data-testid={`reaction-btn-${r.key}-${commentId}`}
            >
              <span className="text-xs leading-none">{r.icon}</span>
              {count > 0 && <span className="tabular-nums">{count}</span>}
            </button>
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 text-[10px] font-medium text-white bg-brand-primary rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 pointer-events-none z-50">
              {r.label}
            </span>
          </div>
        ) : null;
      })}
      {!user && totalReactions === 0 && null}
    </div>
  );
};


const ReviewForm = ({ onSubmit, submitting }) => {
  const [review, setReview] = useState({ praise: '', suggestions: '', additional_comments: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(review);
    setReview({ praise: '', suggestions: '', additional_comments: '' });
  };

  return (
    <form onSubmit={handleSubmit} className="mb-10" data-testid="review-form">
      <p className="text-sm text-text-muted italic mb-4 font-reading">
        Not sure how to start a review? Begin with a line that stayed with you.
      </p>
      <div className="bg-white rounded-xl border border-[#E5E2DC] overflow-hidden">
        <div className="grid md:grid-cols-2 divide-x divide-[#E5E2DC]">
          <div className="p-5">
            <label className="flex items-center gap-2 text-sm font-semibold text-text-primary mb-3">
              <Sparkles className="w-4 h-4 text-brand-accent" />
              The Words That Got to Me
            </label>
            <Textarea
              value={review.praise}
              onChange={(e) => setReview({ ...review, praise: e.target.value })}
              placeholder="Was there a line, image, or moment that stayed with you? Even quoting a single line can help the writer understand what resonated."
              rows={4}
              className="resize-none border-[#E5E2DC] focus:ring-brand-accent/30"
              data-testid="review-praise-input"
            />
          </div>
          <div className="p-5">
            <label className="flex items-center gap-2 text-sm font-semibold text-text-primary mb-3">
              <PenLine className="w-4 h-4 text-brand-primary" />
              Suggestions / Possible Tweaks
            </label>
            <Textarea
              value={review.suggestions}
              onChange={(e) => setReview({ ...review, suggestions: e.target.value })}
              placeholder="If anything felt unclear or could be strengthened, share a gentle suggestion. Small observations are welcome."
              rows={4}
              className="resize-none border-[#E5E2DC] focus:ring-brand-primary/30"
              data-testid="review-suggestions-input"
            />
          </div>
        </div>
        <div className="border-t border-[#E5E2DC] p-5">
          <label className="flex items-center gap-2 text-sm font-semibold text-text-primary mb-3">
            <MessageCircle className="w-4 h-4 text-text-muted" />
            Additional Comments
          </label>
          <Textarea
            value={review.additional_comments}
            onChange={(e) => setReview({ ...review, additional_comments: e.target.value })}
            placeholder="Any other thoughts, questions, or reflections you had while reading."
            rows={2}
            className="resize-none border-[#E5E2DC]"
            data-testid="review-additional-input"
          />
        </div>
        <div className="border-t border-[#E5E2DC] px-5 py-3 bg-surface-alt flex justify-end">
          <Button
            type="submit"
            disabled={submitting || (!review.praise.trim() && !review.suggestions.trim() && !review.additional_comments.trim())}
            className="btn-accent gap-2"
            data-testid="submit-review-button"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Submit Review
          </Button>
        </div>
      </div>
    </form>
  );
};


// Build flat ordered list: [parent, reply1, reply1.1, reply1.2, reply2, ...]
function flattenComments(comments) {
  const byParent = {};
  for (const c of comments) {
    const key = c.parent_id || '__root__';
    if (!byParent[key]) byParent[key] = [];
    byParent[key].push(c);
  }
  const result = [];
  function walk(parentId, depth) {
    const children = byParent[parentId] || [];
    for (const c of children) {
      result.push({ ...c, _depth: depth });
      walk(c.id, depth + 1);
    }
  }
  walk('__root__', 0);
  return result;
}

const ReviewItem = ({ comment, depth, user, onReply, onDelete, onEdit, replyState, setReplyState }) => {
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({
    praise: comment.praise || '',
    suggestions: comment.suggestions || '',
    additional_comments: comment.additional_comments || '',
    content: comment.content || ''
  });
  const [savingEdit, setSavingEdit] = useState(false);

  const isReplying = replyState?.id === comment.id;
  const isStructured = comment.praise || comment.suggestions;

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    await onEdit(comment.id, editData);
    setEditing(false);
    setSavingEdit(false);
  };

  const handleSubmitReply = async () => {
    if (!replyState?.text?.trim()) return;
    setReplyState(prev => ({ ...prev, submitting: true }));
    await onReply(comment.id, replyState.text);
    setReplyState(null);
  };

  return (
    <div style={{ marginLeft: depth > 0 ? `${Math.min(depth, MAX_DEPTH) * 2}rem` : 0 }} data-testid={`review-${comment.id}`} id={`comment-${comment.id}`}>
      {depth > 0 && (
        <div className="flex gap-2">
          <CornerDownRight className="w-4 h-4 text-text-muted mt-3 flex-shrink-0" />
          <div className="flex-1">
            <CommentCard
              comment={comment} user={user} depth={depth} editing={editing} setEditing={setEditing}
              editData={editData} setEditData={setEditData} savingEdit={savingEdit} handleSaveEdit={handleSaveEdit}
              isStructured={isStructured} isReplying={isReplying} setReplyState={setReplyState}
              onDelete={onDelete}
            />
          </div>
        </div>
      )}
      {depth === 0 && (
        <CommentCard
          comment={comment} user={user} depth={depth} editing={editing} setEditing={setEditing}
          editData={editData} setEditData={setEditData} savingEdit={savingEdit} handleSaveEdit={handleSaveEdit}
          isStructured={isStructured} isReplying={isReplying} setReplyState={setReplyState}
          onDelete={onDelete}
        />
      )}
      {isReplying && (
        <div className="mt-3 flex gap-2" style={{ marginLeft: depth > 0 ? '1.5rem' : '2rem' }} data-testid={`reply-form-${comment.id}`}>
          <CornerDownRight className="w-4 h-4 text-text-muted mt-2 flex-shrink-0" />
          <div className="flex-1">
            <Textarea
              value={replyState.text}
              onChange={(e) => setReplyState(prev => ({ ...prev, text: e.target.value }))}
              placeholder={`Reply to ${comment.author_name}...`}
              rows={2}
              className="resize-none border-[#E5E2DC] text-sm mb-2"
              data-testid={`reply-input-${comment.id}`}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setReplyState(null)}>Cancel</Button>
              <Button size="sm" className="btn-accent" disabled={!replyState.text.trim() || replyState.submitting} onClick={handleSubmitReply} data-testid={`submit-reply-${comment.id}`}>
                {replyState.submitting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Reply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CommentCard = ({ comment, user, depth, editing, setEditing, editData, setEditData, savingEdit, handleSaveEdit, isStructured, isReplying, setReplyState, onDelete }) => (
  <div className="bg-white rounded-xl border border-[#E5E2DC] overflow-hidden transition-all duration-300" data-testid={`comment-${comment.id}`}>
    <div className="flex items-center gap-3 p-4 bg-surface-alt/50">
      <Link to={`/writer/${comment.author_id}`}>
        <Avatar className="w-9 h-9">
          <AvatarImage src={comment.author_avatar?.startsWith('/api') ? `${API_URL}${comment.author_avatar}` : comment.author_avatar} />
          <AvatarFallback className="bg-brand-primary text-white text-sm">
            {comment.author_name?.[0]?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
      </Link>
      <div className="flex-1">
        <Link to={`/writer/${comment.author_id}`} className="text-sm font-medium text-text-primary hover:text-brand-primary">
          {comment.author_name}
        </Link>
        <p className="text-xs text-text-muted">{new Date(comment.created_at).toLocaleDateString()}</p>
      </div>
      {user && (
        <div className="flex items-center gap-1">
          {depth < MAX_DEPTH && (
            <Button
              variant="ghost" size="sm"
              onClick={() => setReplyState(isReplying ? null : { id: comment.id, text: '', submitting: false })}
              className="text-text-muted hover:text-brand-accent gap-1"
              data-testid={`reply-btn-${comment.id}`}
            >
              <Reply className="w-3.5 h-3.5" /> Reply
            </Button>
          )}
          {comment.author_id === user.id && (
            <Button variant="ghost" size="sm" onClick={() => setEditing(!editing)} className="text-text-muted hover:text-brand-primary gap-1" data-testid={`edit-comment-${comment.id}`}>
              <Edit className="w-3.5 h-3.5" /> Edit
            </Button>
          )}
          {(comment.author_id === user.id || user.role === 'admin') && (
            <Button variant="ghost" size="sm" onClick={() => onDelete(comment.id)} className="text-text-muted hover:text-red-500" data-testid={`delete-comment-${comment.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      )}
    </div>

    {editing ? (
      <div className="p-4 space-y-3">
        {isStructured ? (
          <>
            <div>
              <label className="text-xs font-semibold text-brand-accent uppercase tracking-wider mb-1 flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> The Words That Got to Me</label>
              <Textarea value={editData.praise} onChange={(e) => setEditData({ ...editData, praise: e.target.value })} rows={3} className="resize-none border-[#E5E2DC] text-sm" data-testid={`edit-praise-${comment.id}`} />
            </div>
            <div>
              <label className="text-xs font-semibold text-brand-primary uppercase tracking-wider mb-1 flex items-center gap-1.5"><PenLine className="w-3 h-3" /> Suggestions / Possible Tweaks</label>
              <Textarea value={editData.suggestions} onChange={(e) => setEditData({ ...editData, suggestions: e.target.value })} rows={3} className="resize-none border-[#E5E2DC] text-sm" data-testid={`edit-suggestions-${comment.id}`} />
            </div>
            <div>
              <label className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-1 flex items-center gap-1.5"><MessageCircle className="w-3 h-3" /> Additional Comments</label>
              <Textarea value={editData.additional_comments} onChange={(e) => setEditData({ ...editData, additional_comments: e.target.value })} rows={2} className="resize-none border-[#E5E2DC] text-sm" data-testid={`edit-additional-${comment.id}`} />
            </div>
          </>
        ) : (
          <Textarea value={editData.content} onChange={(e) => setEditData({ ...editData, content: e.target.value })} rows={3} className="resize-none border-[#E5E2DC] text-sm" data-testid={`edit-content-${comment.id}`} />
        )}
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
          <Button size="sm" className="btn-accent" disabled={savingEdit} onClick={handleSaveEdit} data-testid={`save-edit-${comment.id}`}>
            {savingEdit ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Save
          </Button>
        </div>
      </div>
    ) : (
      <>
        {isStructured ? (
          <>
            <div className="grid md:grid-cols-2 divide-x divide-[#E5E2DC]">
              {comment.praise && (
                <div className="p-4">
                  <p className="text-xs font-semibold text-brand-accent uppercase tracking-wider mb-2 flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> The Words That Got to Me</p>
                  <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-wrap"><MentionText text={comment.praise} /></p>
                </div>
              )}
              {comment.suggestions && (
                <div className="p-4">
                  <p className="text-xs font-semibold text-brand-primary uppercase tracking-wider mb-2 flex items-center gap-1.5"><PenLine className="w-3 h-3" /> Suggestions / Possible Tweaks</p>
                  <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-wrap"><MentionText text={comment.suggestions} /></p>
                </div>
              )}
            </div>
            {(comment.additional_comments || (!comment.praise && !comment.suggestions && comment.content)) && (
              <div className="border-t border-[#E5E2DC] p-4">
                <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-wrap"><MentionText text={comment.additional_comments || comment.content} /></p>
              </div>
            )}
          </>
        ) : (
          <div className="p-4">
            <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-wrap"><MentionText text={comment.content} /></p>
          </div>
        )}
        {comment.edited_at && (
          <div className="px-4 pb-2">
            <span className="text-xs text-text-muted italic">edited {new Date(comment.edited_at).toLocaleDateString()}</span>
          </div>
        )}

        {/* Review Reactions — only on top-level structured reviews */}
        {depth === 0 && isStructured && (
          <div className="border-t border-[#E5E2DC] px-4 py-2.5">
            <ReviewReactions commentId={comment.id} />
          </div>
        )}
      </>
    )}
  </div>
);


const CommentSection = ({ comments, storyId, chapterIndex, chapterTitle, onCommentAdded, onCommentDeleted }) => {
  const { user, api } = useAuth();
  const [showComments, setShowComments] = useState(true);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [replyState, setReplyState] = useState(null);

  const handleSubmitComment = async (review) => {
    if (!user) return;
    setSubmittingComment(true);
    try {
      const commentData = {
        story_id: storyId,
        praise: review.praise,
        suggestions: review.suggestions,
        additional_comments: review.additional_comments,
        content: [review.praise, review.suggestions, review.additional_comments].filter(Boolean).join('\n\n'),
        chapter_index: chapterIndex
      };
      const res = await api.post('/comments', commentData);
      onCommentAdded(res.data);
    } catch (error) {
      // Non-critical error suppressed
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleSubmitReply = async (parentId, replyText) => {
    if (!user || !replyText.trim()) return;
    try {
      const res = await api.post('/comments', {
        story_id: storyId,
        content: replyText,
        parent_id: parentId,
        chapter_index: chapterIndex
      });
      onCommentAdded(res.data);
    } catch (error) {
      // Non-critical error suppressed
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await api.delete(`/comments/${commentId}`);
      onCommentDeleted(commentId);
    } catch (error) {
      // Non-critical error suppressed
    }
  };

  const handleEditComment = async (commentId, editData) => {
    try {
      const res = await api.put(`/comments/${commentId}`, {
        story_id: storyId,
        content: editData.content || [editData.praise, editData.suggestions, editData.additional_comments].filter(Boolean).join('\n\n'),
        praise: editData.praise,
        suggestions: editData.suggestions,
        additional_comments: editData.additional_comments
      });
      onCommentAdded(res.data, true);
    } catch (error) {
      // Non-critical error suppressed
    }
  };

  return (
    <section className={showComments ? '' : 'hidden'} data-testid="reviews-section">
      <div 
        className="flex items-center justify-between cursor-pointer mb-6"
        onClick={() => setShowComments(!showComments)}
      >
        <div>
          <h2 className="font-serif text-2xl font-semibold text-text-primary">
            Reviews ({comments.filter(c => !c.parent_id).length})
          </h2>
          {chapterTitle && (
            <p className="text-sm text-text-muted mt-1">
              for <span className="font-medium text-text-secondary">{chapterTitle}</span>
            </p>
          )}
        </div>
      </div>

      {user ? (
        <ReviewForm onSubmit={handleSubmitComment} submitting={submittingComment} />
      ) : (
        <div className="text-center py-8 mb-8 bg-white rounded-xl border border-[#E5E2DC]">
          <p className="text-text-secondary mb-3">Sign in to leave a review</p>
          <Link to="/login">
            <Button className="btn-primary">Sign In</Button>
          </Link>
        </div>
      )}

      <div className="space-y-4" data-testid="reviews-list">
        {comments.length === 0 ? (
          <p className="text-center text-text-muted py-8">
            No reviews yet. Be the first to share your thoughts!
          </p>
        ) : (
          flattenComments(comments).map((comment) => (
            <ReviewItem
              key={comment.id}
              comment={comment}
              depth={comment._depth}
              user={user}
              onReply={handleSubmitReply}
              onDelete={handleDeleteComment}
              onEdit={handleEditComment}
              replyState={replyState}
              setReplyState={setReplyState}
            />
          ))
        )}
      </div>
    </section>
  );
};

export default CommentSection;
